import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Play, ArrowRight, Sparkles, Award, Clock, Users } from 'lucide-react';
import { Link } from 'react-router-dom';
import { apiService } from '../services/apiService';

const Hero = () => {
  const [intro, setIntro] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showContent, setShowContent] = useState(true);
  const [videoLoaded, setVideoLoaded] = useState(false);
  const videoRef = useRef(null);

  useEffect(() => {
    const fetchIntro = async () => {
      try {
        const response = await apiService.getIntro();
        if (response.estado === 1) {
          setIntro(response.data);
        }
      } catch (error) {
        console.error('Error cargando intro:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchIntro();
  }, []);

  // Ocultar contenido después de 6 segundos
  useEffect(() => {
    const timer = setTimeout(() => {
      setShowContent(false);
    }, 6000); // 6 segundos

    return () => clearTimeout(timer);
  }, []);

  const handleVideoLoad = () => {
    setVideoLoaded(true);
  };

  // Stats
  const stats = [
    { value: '10+', label: 'Años de Experiencia', icon: Award },
    { value: '500+', label: 'Eventos Realizados', icon: Clock },
    { value: '99%', label: 'Clientes Satisfechos', icon: Users }
  ];

  if (loading) {
    return (
      <section className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <motion.div
            className="w-16 h-16 border-4 border-yellow-600 border-t-yellow-500 rounded-full"
            animate={{ rotate: 360 }}
            transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          />
          <p className="text-yellow-500 mt-4 text-sm">Cargando...</p>
        </div>
      </section>
    );
  }

  return (
    <section className="relative min-h-screen w-full overflow-hidden bg-black">
      {/* Background: Video o Imagen */}
      <div className="absolute inset-0 w-full h-full">
        {intro?.tipo === 'video' && intro.archivo_url_completa ? (
          <>
            <video
              ref={videoRef}
              autoPlay
              loop
              muted
              playsInline
              className="absolute inset-0 w-full h-full object-cover"
              onLoadedData={handleVideoLoad}
            >
              <source src={intro.archivo_url_completa} type="video/mp4" />
              Tu navegador no soporta videos HTML5
            </video>
            {/* Overlay oscuro - más oscuro cuando no hay contenido */}
            <div className={`absolute inset-0 transition-all duration-1000 ${
              showContent ? 'bg-black/60' : 'bg-black/40'
            }`}></div>
          </>
        ) : intro?.archivo_url_completa ? (
          <>
            <img
              src={intro.archivo_url_completa}
              alt="ROTMANS Hero"
              className="absolute inset-0 w-full h-full object-cover"
            />
            {/* Overlay oscuro */}
            <div className={`absolute inset-0 transition-all duration-1000 ${
              showContent ? 'bg-black/70' : 'bg-black/50'
            }`}></div>
          </>
        ) : (
          /* Fallback si no hay media */
          <div className="absolute inset-0 bg-gradient-to-br from-black via-gray-900 to-black">
            <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,_var(--tw-gradient-stops))] from-yellow-900/20 via-transparent to-transparent"></div>
          </div>
        )}
      </div>

      {/* Partículas doradas animadas */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {Array.from({ length: 20 }).map((_, i) => (
          <motion.div
            key={i}
            className="absolute w-1 h-1 bg-yellow-500 rounded-full"
            style={{
              left: `${Math.random() * 100}%`,
              top: `${Math.random() * 100}%`,
            }}
            animate={{
              y: [0, -100, 0],
              opacity: [0, 1, 0],
              scale: [0, 1, 0],
            }}
            transition={{
              duration: Math.random() * 3 + 2,
              repeat: Infinity,
              delay: Math.random() * 2,
              ease: "easeInOut"
            }}
          />
        ))}
      </div>

      {/* Contenido principal - Se desvanece después de 6 segundos */}
      <AnimatePresence>
        {showContent && (
          <motion.div
            key="hero-content"
            initial={{ opacity: 1 }}
            exit={{ opacity: 0, y: -20 }}
            transition={{ duration: 0.8 }}
            className="relative z-10 min-h-screen flex flex-col items-center justify-center px-4 sm:px-6 lg:px-8"
          >
            <div className="max-w-6xl mx-auto text-center">
              
              {/* Badge superior */}
              <motion.div
                initial={{ opacity: 0, y: -30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
                className="inline-flex items-center gap-2 bg-yellow-600/20 backdrop-blur-sm border border-yellow-600/40 rounded-full px-6 py-2 mb-8"
              >
                <Sparkles className="w-4 h-4 text-yellow-500" />
                <span className="text-yellow-500 text-sm font-medium">
                  Experiencia y Tecnología en Audio, Luces y Pantallas
                </span>
              </motion.div>

              {/* Título principal */}
              <motion.h1
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold mb-6"
              >
                <span className="block text-white mb-2">ROTMANS</span>
                <span className="block bg-gradient-to-r from-yellow-400 via-yellow-500 to-yellow-600 bg-clip-text text-transparent">
                  Producciones & Eventos
                </span>
              </motion.h1>

              {/* Subtítulo */}
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.4 }}
                className="text-lg sm:text-xl md:text-2xl text-gray-300 mb-8 max-w-3xl mx-auto leading-relaxed"
              >
                Transformamos tus eventos en experiencias inolvidables con 
                <span className="text-yellow-500 font-semibold"> tecnología de punta </span>
                y un equipo humano comprometido con la excelencia.
              </motion.p>

              {/* Botones de acción */}
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
                className="flex flex-col sm:flex-row gap-4 justify-center items-center mb-12"
              >
                <Link
                  to="/contacto"
                  className="group relative inline-flex items-center gap-3 bg-gradient-to-r from-yellow-500 to-yellow-600 text-black px-8 py-4 rounded-full font-bold text-lg shadow-2xl hover:shadow-yellow-500/25 transition-all duration-300 overflow-hidden"
                >
                  <span className="relative z-10">Cotiza tu Evento</span>
                  <ArrowRight className="w-5 h-5 relative z-10 group-hover:translate-x-1 transition-transform" />
                  <div className="absolute inset-0 bg-gradient-to-r from-yellow-400 to-yellow-500 opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                </Link>

                <Link
                  to="/eventos"
                  className="group inline-flex items-center gap-3 bg-black/50 backdrop-blur-sm border-2 border-yellow-600/50 text-yellow-500 px-8 py-4 rounded-full font-semibold text-lg hover:bg-yellow-600/20 hover:border-yellow-500 transition-all duration-300"
                >
                  <Play className="w-5 h-5" />
                  <span>Ver Eventos</span>
                </Link>
              </motion.div>

              {/* Stats */}
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
                className="grid grid-cols-1 sm:grid-cols-3 gap-6 max-w-3xl mx-auto"
              >
                {stats.map((stat, index) => {
                  const Icon = stat.icon;
                  return (
                    <motion.div
                      key={stat.label}
                      initial={{ opacity: 0, scale: 0.8 }}
                      animate={{ opacity: 1, scale: 1 }}
                      transition={{ delay: 1 + index * 0.1, duration: 0.5 }}
                      className="bg-black/40 backdrop-blur-sm border border-yellow-600/30 rounded-2xl p-6 hover:border-yellow-500/50 transition-colors duration-300"
                    >
                      <Icon className="w-8 h-8 text-yellow-500 mx-auto mb-3" />
                      <div className="text-3xl sm:text-4xl font-bold text-white mb-1">
                        {stat.value}
                      </div>
                      <div className="text-sm text-gray-400">{stat.label}</div>
                    </motion.div>
                  );
                })}
              </motion.div>
            </div>

            {/* Scroll indicator */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 1.5, duration: 0.5 }}
              className="absolute bottom-8 left-1/2 transform -translate-x-1/2"
            >
              <motion.div
                animate={{ y: [0, 10, 0] }}
                transition={{ duration: 1.5, repeat: Infinity }}
                className="w-6 h-10 border-2 border-yellow-600/50 rounded-full flex justify-center"
              >
                <motion.div
                  className="w-1.5 h-3 bg-yellow-500 rounded-full mt-2"
                  animate={{ opacity: [1, 0.3, 1] }}
                  transition={{ duration: 1.5, repeat: Infinity }}
                />
              </motion.div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Gradientes decorativos */}
      <div className="absolute top-0 left-0 w-full h-full pointer-events-none overflow-hidden">
        <div className="absolute top-0 left-0 w-96 h-96 bg-yellow-600/10 rounded-full filter blur-3xl"></div>
        <div className="absolute bottom-0 right-0 w-96 h-96 bg-yellow-600/10 rounded-full filter blur-3xl"></div>
      </div>
    </section>
  );
};

export default Hero;